import React from 'react'
import { formatPrice } from '#app/shared/lib/price.ts'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { CarSortLabelEnum } from '../../../../../types/enums'

interface FilterTagsProps {
  filters: Record<string, string[]>
  currentSort: string
  onRemoveFilter: (filter: string) => void
  onClearAll: () => void
  onRemoveSort: () => void
}

export const FilterTags: React.FC<FilterTagsProps> = ({
  filters,
  currentSort,
  onRemoveFilter,
  onClearAll,
  onRemoveSort,
}) => {
  const handleRemoveFilter = (filterKey: string, value: string) => {
    if (filterKey === 'sort') {
      onRemoveSort()
    } else {
      onRemoveFilter(`${filterKey}:${value}`)
    }
  }

  const renderFilterTag = (filterKey: string, value: string) => {
    let displayValue: string

    switch (filterKey) {
      case 'sort':
        displayValue = CarSortLabelEnum[value as keyof typeof CarSortLabelEnum]
        break
      case 'priceMin':
        displayValue = `Precio desde: ${formatPrice(Number(value) || 0)}`
        break
      case 'priceMax':
        displayValue = `Precio hasta: ${formatPrice(Number(value) || 0)}`
        break
      case 'financingMin':
        displayValue = `Financiación desde: ${formatPrice(Number(value) || 0)}`
        break
      case 'financingMax':
        displayValue = `Financiación hasta: ${formatPrice(Number(value) || 0)}`
        break
      case 'deducibleIva':
        displayValue = 'IVA deducible'
        break
      case 'kmMin':
        displayValue = `Km desde: ${value}`
        break
      case 'kmMax':
        displayValue = `Km hasta: ${value}`
        break
      case 'yearMin':
        displayValue = `Año desde: ${value}`
        break
      case 'yearMax':
        displayValue = `Año hasta: ${value}`
        break
      case 'powerMin':
        displayValue = `Potencia desde: ${value}`
        break
      case 'powerMax':
        displayValue = `Potencia hasta: ${value}`
        break
      case 'puertas':
        displayValue = `Puertas: ${value}`
        break
      case 'plazas':
        displayValue = `Plazas: ${value}`
        break
      case 'traccion':
        displayValue = `Tracción: ${value}`
        break
      case 'combustible':
        displayValue = `Combustible: ${value}`
        break
      case 'manufacturerBatteryWarranty':
        displayValue = 'Garantía del fabricante'
        break
      default:
        displayValue = value
    }

    return (
      <div
        key={`${filterKey}-${value}`}
        className={`
          filter-tag flex items-center rounded border border-neutral-100
          bg-neutral-white px-2 py-1
        `}
      >
        <span className="text-subtitle1-bold">{displayValue}</span>
        <button
          onClick={() => handleRemoveFilter(filterKey, value)}
          className={`ml-2`}
        >
          <img
            src="/img/Filter_Del.svg"
            alt="Eliminar filtro"
            className={`text-red-500`}
          />
        </button>
      </div>
    )
  }

  const hasAnyFilter = () => {
    return (
      Object.keys(filters).some(
        (filterKey) => filters[filterKey] && filters[filterKey].length > 0,
      ) || currentSort !== ''
    )
  }

  return (
    <MainContainer
      className={`
        px-10

        lg:px-2
      `}
    >
      <div className="flex flex-wrap items-center gap-2 pt-4">
        {Object.keys(filters).map((filterKey) => {
          const filterValues = filters[filterKey] || []

          // Special handling for combustible filters
          if (filterKey === 'combustible' && filterValues.length > 0) {
            const hybridTypes = ['Híbrido', 'Híbrido enchufable', 'Mild Hybrid']
            const hasAllHybrids = hybridTypes.every((type) =>
              filterValues.includes(type),
            )
            const hasOnlyHybrids =
              filterValues.length === hybridTypes.length && hasAllHybrids

            if (hasOnlyHybrids) {
              // Show only one "Híbridos" tag and handle removal of all hybrid types
              return (
                <div
                  key="combustible-hybrids"
                  className={`
                    filter-tag flex items-center rounded border
                    border-neutral-100 bg-neutral-white px-2 py-1
                  `}
                >
                  <span className="text-subtitle1-bold">
                    Combustible: Híbrido, Híbrido enchufable, Mild Hybrid
                  </span>
                  <button
                    onClick={() => {
                      // Remove all hybrid types at once
                      onRemoveFilter('combustible:ALL_HYBRIDS')
                    }}
                    className={`ml-2`}
                  >
                    <img
                      src="/img/Filter_Del.svg"
                      alt="Eliminar filtro"
                      className={`text-red-500`}
                    />
                  </button>
                </div>
              )
            } else {
              // Show individual combustible tags
              return filterValues.map((value) =>
                renderFilterTag(filterKey, value),
              )
            }
          }

          return filterValues.length > 0
            ? filterValues.map((value) => renderFilterTag(filterKey, value))
            : null
        })}
        {currentSort && renderFilterTag('sort', currentSort)}
        {hasAnyFilter() && (
          <button
            onClick={onClearAll}
            className={`
              flex items-center text-subtitle2-medium text-neutral-400
            `}
          >
            <img
              src="/img/Rubbish_Bin.svg"
              alt="Eliminar filtro"
              className={`mr-2 h-4 w-4 object-contain`}
            />
            Quitar filtros
          </button>
        )}
      </div>
    </MainContainer>
  )
}
